import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CategorySelectPopup from "../../../components/CategorySelectPopup"; // Import Category Popup

import { useParams, useNavigate } from "react-router-dom";
import { db, auth, storage } from "../../../firebase-config";
import { doc, getDoc, updateDoc, collection, getDocs, setDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import DeleteIcon from "@mui/icons-material/Delete";
import UserSelectPopup from "../../../components/UserSelectPopup";
import CompanySelectPopup from "../../../components/CompanySelectPopup";

const AdminProductEdit = () => {
  const { productId } = useParams();
  const [categoryPopupOpen, setCategoryPopupOpen] = useState(false); // Controls Category Popup
const [categories, setCategories] = useState([]); // Stores all available categories
const [selectedCategory, setSelectedCategory] = useState(""); // Selected category

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [publicProduct, setPublicProduct] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [changePrice, setChangePrice] = useState(false);
  const [userPricing, setUserPricing] = useState([]);
  const [companyPricing, setCompanyPricing] = useState([]);
  const [options, setOptions] = useState([]);
  const [userPopupOpen, setUserPopupOpen] = useState(false);
  const [companyPopupOpen, setCompanyPopupOpen] = useState(false);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;


  useEffect(() => {
    if (!currentUser) return;
  
    const fetchCategories = async () => {
      try {
        const categoriesRef = collection(db, "Categories");
        const categoriesSnap = await getDocs(categoriesRef);
        setCategories(categoriesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
  
    fetchCategories();
  }, [currentUser]);

  
  useEffect(() => {
    if (!currentUser) return;

    const fetchProduct = async () => {
      try {
        const productRef = doc(db, "products", productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          setProduct(productData);
          setPublicProduct(productData.public || false);
        } else {
          setError("Product not found.");
        }

        // Fetch user-specific pricing
        const userPricingRef = collection(db, "products", productId, "pricing");
        const userPricingSnap = await getDocs(userPricingRef);
        setUserPricing(userPricingSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

        // Fetch company-specific pricing
        const companyPricingRef = collection(db, "products", productId, "companyPricing");
        const companyPricingSnap = await getDocs(companyPricingRef);
        setCompanyPricing(companyPricingSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

        // Fetch product options
        const optionsRef = collection(db, "products", productId, "options");
        const optionsSnap = await getDocs(optionsRef);
        setOptions(optionsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product.");
      } finally {
        setLoading(false);
      }
    };

    const checkAdminStatus = async () => {
      const adminRef = doc(db, "admins", currentUser.uid);
      const adminSnap = await getDoc(adminRef);
      setIsAdmin(adminSnap.exists());
    };

    fetchProduct();
    checkAdminStatus();
  }, [currentUser, productId]);

  const handleSaveChanges = async () => {
    if (!isAdmin) {
      setError("You do not have permission to edit this product.");
      return;
    }

    try {
      let imageUrl = product.imageUrl;
      if (newImage) {
        const storageRef = ref(storage, `productImages/${productId}`);
        await uploadBytes(storageRef, newImage);
        imageUrl = await getDownloadURL(storageRef);
      }

      const productRef = doc(db, "products", productId);
      await updateDoc(productRef, {
        name: product.name,
        description: product.description,
        defaultPrice: parseFloat(product.defaultPrice),
        
        category: selectedCategory, // Save selected category
        
        //category: product.category,
        imageUrl: imageUrl,
        public: publicProduct,
        visibleToUsers: selectedUsers.map((user) => user.id),
        visibleToCompanies: selectedCompanies.map((company) => company.id),
      });

      alert("Product updated successfully!");
      navigate("/admin/products");
    } catch (err) {
      console.error("Error updating product:", err);
      setError("Failed to update product.");
    }
  };

  const addOption = () => {
    setOptions([...options, { name: "", description: "", additionalPrice: 0 }]);
  };

  const updateOption = (index, key, value) => {
    const updatedOptions = [...options];
    updatedOptions[index][key] = value;
    setOptions(updatedOptions);
  };

  const removeOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Edit Product
      </Typography>

      <TextField label="Product Name" fullWidth name="name" value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} sx={{ mb: 2 }} />

      <TextField label="Description" fullWidth multiline rows={3} name="description" value={product.description} onChange={(e) => setProduct({ ...product, description: e.target.value })} sx={{ mb: 2 }} />

      <TextField label="Default Price" fullWidth type="number" name="defaultPrice" value={product.defaultPrice} onChange={(e) => setProduct({ ...product, defaultPrice: e.target.value })} sx={{ mb: 2 }} />

      <FormControlLabel control={<Checkbox checked={publicProduct} onChange={(e) => setPublicProduct(e.target.checked)} />} label="Public Product" />



      <FormControl sx={{ mb: 2, width: "100%" }}>
  <InputLabel>Category</InputLabel>
  <Select
    value={selectedCategory}
    onChange={(e) => setSelectedCategory(e.target.value)}
  >
    {categories.map((category) => (
      <MenuItem key={category.id} value={category.name}>
        {category.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>

<Button variant="contained" onClick={() => setCategoryPopupOpen(true)} sx={{ mb: 2 }}>
  Select Category
</Button>



      {/* User & Company Selection */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Restrict Visibility (Select Users/Companies)</Typography>
       


        <Box sx={{ mb: 2 }}>
  <Typography variant="h6">Visibility & Pricing</Typography>

  {/* Buttons to Select Users & Companies */}
  <Button
    variant="contained"
    sx={{ mr: 2 }}
    onClick={() => setUserPopupOpen(true)}
  >
    Select Users
  </Button>
  <Button variant="contained" onClick={() => setCompanyPopupOpen(true)}>
    Select Companies
  </Button>

  {/* Selected Users List */}
  {selectedUsers.length > 0 && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2">Selected Users:</Typography>
      <List>
        {selectedUsers.map((user, index) => (
          <ListItem key={user.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ListItemText primary={user.email} />
            
            {/* "Change Pricing" Checkbox for User */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!user.customPrice}
                  onChange={(e) => {
                    const updatedUsers = [...selectedUsers];
                    updatedUsers[index].customPrice = e.target.checked ? user.price || product.defaultPrice : null;
                    setSelectedUsers(updatedUsers);
                  }}
                />
              }
              label="Change Pricing"
            />

            {/* Pricing Input (Only Show if Checkbox is Checked) */}
            {("customPrice" in user) && user.customPrice !== null && (
              <TextField
                label="Custom Price"
                type="number"
                value={user.customPrice}
                onChange={(e) => {
                  const updatedUsers = [...selectedUsers];
                  updatedUsers[index].customPrice = parseFloat(e.target.value) || 0;
                  setSelectedUsers(updatedUsers);
                }}
                sx={{ width: "100px" }}
              />
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  )}

  {/* Selected Companies List */}
  {selectedCompanies.length > 0 && (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2">Selected Companies:</Typography>
      <List>
        {selectedCompanies.map((company, index) => (
          <ListItem key={company.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ListItemText primary={company.name} />

            {/* "Change Pricing" Checkbox for Company */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!company.customPrice}
                  onChange={(e) => {
                    const updatedCompanies = [...selectedCompanies];
                    updatedCompanies[index].customPrice = e.target.checked ? company.price || product.defaultPrice : null;
                    setSelectedCompanies(updatedCompanies);
                  }}
                />
              }
              label="Change Pricing"
            />

            {/* Pricing Input (Only Show if Checkbox is Checked) */}
            {("customPrice" in company) && company.customPrice !== null && (
              <TextField
                label="Custom Price"
                type="number"
                value={company.customPrice}
                onChange={(e) => {
                  const updatedCompanies = [...selectedCompanies];
                  updatedCompanies[index].customPrice = parseFloat(e.target.value) || 0;
                  setSelectedCompanies(updatedCompanies);
                }}
                sx={{ width: "100px" }}
              />
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  )}
</Box>



<CategorySelectPopup
  open={categoryPopupOpen}
  onClose={() => setCategoryPopupOpen(false)}
  onSelectCategory={(selected) => setSelectedCategory(selected.length > 0 ? selected[0].name : "")}
  selectedCategories={[{ name: selectedCategory }]}
/>


{/* User & Company Selection Popups */}
<UserSelectPopup
  open={userPopupOpen}
  onClose={() => setUserPopupOpen(false)}
  onSelectUser={(selected) => setSelectedUsers(selected)}
  selectedUsers={selectedUsers}
/>

<CompanySelectPopup
  open={companyPopupOpen}
  onClose={() => setCompanyPopupOpen(false)}
  onSelectCompany={(selected) => setSelectedCompanies(selected)}
  selectedCompanies={selectedCompanies}
/>


{/* User & Company Selection Popups */}
<UserSelectPopup
  open={userPopupOpen}
  onClose={() => setUserPopupOpen(false)}
  onSelectUser={(selected) => setSelectedUsers(selected)}
  selectedUsers={selectedUsers}
/>

<CompanySelectPopup
  open={companyPopupOpen}
  onClose={() => setCompanyPopupOpen(false)}
  onSelectCompany={(selected) => setSelectedCompanies(selected)}
  selectedCompanies={selectedCompanies}
/>

      </Box>


      {/* Options Section */}
      <Typography variant="h6" sx={{ mt: 2 }}>Product Options</Typography>
      {options.map((option, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <TextField label="Option Name" value={option.name} onChange={(e) => updateOption(index, "name", e.target.value)} />
          <TextField label="Description" value={option.description} onChange={(e) => updateOption(index, "description", e.target.value)} />
          <TextField label="Additional Price" type="number" value={option.additionalPrice} onChange={(e) => updateOption(index, "additionalPrice", parseFloat(e.target.value) || 0)} />
          <IconButton onClick={() => removeOption(index)}><DeleteIcon /></IconButton>
        </Box>
      ))}
      <Button onClick={addOption}>Add Option</Button>

      <Button variant="contained" color="primary" onClick={handleSaveChanges} sx={{ mt: 3 }}>
        Save Changes
      </Button>
    </Container>
  );
};

export default AdminProductEdit;
